import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconDelete, IconRight, IconDeleteDisabled } from "@assets";
import { Button, Form, Input, message, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { CheckOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import { api } from "@api";
import { useTranslation } from "react-i18next";

const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }

    @media (max-width: 480px){
  flex-wrap:wrap;
  justify-content:start;
}
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
    white-space:nowrap;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  padding-bottom: 16px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
  
  @media (max-width: 480px){
flex-direction:column;
gap:10px;
justify-content:start;
align-items:start;
}
`;

const ItemCard = styled.div`
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  background-color: #fff;

  .ant-form {
    flex: 1;
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 0;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 24px;

      :last-child {
        margin: 0;
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }
`;

const SpinWrapper = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Item = ({
  item,
  disabledDelete,
  handleUpdateClicked,
  handleDeleteClicked,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldValue("name", item.name);
    form.setFieldValue("values", item.values);

    // eslint-disable-next-line
  }, [item]);

  const onFieldsChange = () => {
    handleUpdateClicked({
      id: item.id,
      ...form.getFieldsValue(),
    });
  };

  const {t} = useTranslation();

  return (
    <ItemCard>
      <Form layout="vertical" form={form} onFieldsChange={onFieldsChange}>
        <Form.Item
          name={"name"}
          label={t('option_name')}
          rules={[
            {
              required: true,
              message: t('option_name_required'),
            },
          ]}
        >
          <Input placeholder={t('option_name')} />
        </Form.Item>
        <Form.Item
          name={"values"}
          label={t('value')}
          rules={[
            {
              required: true,
              message: t('value_required'),
            },
          ]}
        >
          <Input placeholder={t("value_use_comma")} />
        </Form.Item>
      </Form>
      {disabledDelete ? (
        <IconDeleteDisabled className="icon-delete" />
      ) : (
        <IconDelete
          className="icon-delete"
          onClick={() => handleDeleteClicked(item)}
        />
      )}
    </ItemCard>
  );
};

const CreateProductOptions = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([
    {
      id: uuid(),
      name: "",
      values: "",
    },
  ]);
  const [disabledSave, setDisabledSave] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const {t} = useTranslation();

  useEffect(() => {
    let _isEmpty = data.find((x) => isEmpty(x.name) || isEmpty(x.values));
    setDisabledSave(_isEmpty || data.length === 0);

    // eslint-disable-next-line
  }, [data]);

  const isEmpty = (value) => {
    return !value || value?.trim() === "";
  };

  const createOptions = (data) => {
    let payload = {
      variants: data.map(({ name, values }) => {
        return {
          name,
          values: values.split(",").map((v) => v.trim()),
        };
      }),
    };
    api
      .createProductOptions(payload)
      .then(() => {
        message.success(t('create_product_option'));
        goBack();
      })
      .catch((error) => {
        setConfirmLoading(false);
        message.error(error);
      });
  };

  const handleSave = () => {
    if (!confirmLoading) {
      setConfirmLoading(true);
      createOptions(data);
    }
  };

  const handleAddOptionClicked = () => {
    setData((prev) => {
      return [
        ...prev,
        {
          id: uuid(),
          name: "",
          values: "",
        },
      ];
    });
  };

  const handleUpdateClicked = (item) => {
    setData((prev) =>
      prev.map((x) => {
        if (x.id === item.id) {
          return item;
        }
        return x;
      })
    );
  };

  const handleDeleteClicked = (item) => {
    if (data.length === 1) {
      return;
    }
    setData((prev) => prev.filter((x) => x.id !== item.id));
  };

  const goBack = () => {
    navigate(-1);
  };

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          {t('back')}
        </Button>
        <TitleWrapper>
          <GrayTitle>{t('products')}</GrayTitle>
          <IconRight />
          <Title>{t("create_prod_option")}</Title>
        </TitleWrapper>
        <Button
          type="primary"
          disabled={disabledSave}
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={confirmLoading}
        >
          {t('create')}
        </Button>
      </Topbar>
      <Container>
        <TitleBar>
          <Title>{`${t('product_options_eg')}`}</Title>
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={handleAddOptionClicked}
          >
            {t('add_option')}
          </Button>
        </TitleBar>
        {data.map((item, key) => (
          <Item
            key={key}
            item={item}
            disabledDelete={data.length === 1}
            handleUpdateClicked={handleUpdateClicked}
            handleDeleteClicked={handleDeleteClicked}
          />
        ))}
      </Container>
    </RootContainer>
  );
};

export default CreateProductOptions;
